import React, { useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import TopBar from "../sidebar/TopBar";
import { fetchData, ImageUpload } from "../../../helper/ApiCall";
import { END_POINT, METHODS } from "../../../helper/helper";

// ─── DESIGN TOKENS ────────────────────────────────────────────────────────────
const c = {
  bg: "#F7F8FC",
  surface: "#FFFFFF",
  border: "#E8ECF4",
  text: "#1A202C",
  textMuted: "#718096",
  textFaint: "#A0AEC0",
  accent: "#4F46E5",
  accentLight: "#EEF2FF",
  accentBorder: "#C7D2FE",
  danger: "#DC2626",
  dangerLight: "#FEE2E2",
  success: "#059669",
  successLight: "#D1FAE5",
};

// ─── TOAST ────────────────────────────────────────────────────────────────────
const Toast = ({ msg }) => {
  if (!msg.text) return null;
  const s = msg.type === "error"
    ? { bg: c.dangerLight, color: c.danger, border: "#FECACA", icon: "✕" }
    : { bg: c.successLight, color: c.success, border: "#A7F3D0", icon: "✓" };
  return (
    <div style={{
      position: "fixed", top: 20, right: 24, zIndex: 999,
      padding: "12px 18px", borderRadius: "10px", fontSize: "13px", fontWeight: 500,
      display: "flex", alignItems: "center", gap: "8px",
      background: s.bg, color: s.color, border: `1px solid ${s.border}`,
      boxShadow: "0 4px 20px rgba(0,0,0,0.1)", animation: "slideIn 0.2s ease",
    }}>
      <span style={{ fontWeight: 700 }}>{s.icon}</span> {msg.text}
    </div>
  );
};

// ─── FIELD WRAPPER ────────────────────────────────────────────────────────────
const Field = ({ label, required, hint, children }) => (
  <div style={{ marginBottom: "20px" }}>
    <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: "6px" }}>
      <label style={{ fontSize: "12px", fontWeight: 600, color: c.textMuted, letterSpacing: "0.05em", textTransform: "uppercase" }}>
        {label}{required && <span style={{ color: c.accent }}> *</span>}
      </label>
      {hint && <span style={{ fontSize: "11px", color: c.textFaint }}>{hint}</span>}
    </div>
    {children}
  </div>
);

// ─── RICH TEXT EDITOR ─────────────────────────────────────────────────────────
const TOOLBAR = [
  { cmd: "bold",          icon: "B",   title: "Bold",          style: { fontWeight: 700 } },
  { cmd: "italic",        icon: "I",   title: "Italic",        style: { fontStyle: "italic" } },
  { cmd: "underline",     icon: "U",   title: "Underline",     style: { textDecoration: "underline" } },
  { cmd: "strikeThrough", icon: "S",   title: "Strikethrough", style: { textDecoration: "line-through" } },
  { type: "sep" },
  { cmd: "formatBlock",   arg: "H2",   icon: "H2",  title: "Heading 2" },
  { cmd: "formatBlock",   arg: "H3",   icon: "H3",  title: "Heading 3" },
  { cmd: "formatBlock",   arg: "P",    icon: "¶",   title: "Paragraph" },
  { type: "sep" },
  { cmd: "insertUnorderedList", icon: "• —", title: "Bullet List" },
  { cmd: "insertOrderedList",   icon: "1.",  title: "Numbered List" },
  { type: "sep" },
  { cmd: "justifyLeft",   icon: "⬅",  title: "Align Left" },
  { cmd: "justifyCenter", icon: "≡",   title: "Align Center" },
  { cmd: "justifyRight",  icon: "➡",  title: "Align Right" },
  { type: "sep" },
  { cmd: "createLink",    icon: "🔗",  title: "Insert Link", special: "link" },
  { cmd: "removeFormat",  icon: "✕",   title: "Clear Format" },
];

const RichEditor = ({ value, onChange }) => {
  const editorRef = useRef(null);
  const [activeFormats, setActiveFormats] = useState({});

  const exec = useCallback((cmd, arg) => {
    if (cmd === "createLink") {
      const url = window.prompt("Enter URL:", "https://");
      if (url) document.execCommand("createLink", false, url);
    } else {
      document.execCommand(cmd, false, arg || null);
    }
    editorRef.current?.focus();
    updateActiveFormats();
  }, []);

  const updateActiveFormats = () => {
    setActiveFormats({
      bold: document.queryCommandState("bold"),
      italic: document.queryCommandState("italic"),
      underline: document.queryCommandState("underline"),
      strikeThrough: document.queryCommandState("strikeThrough"),
      insertUnorderedList: document.queryCommandState("insertUnorderedList"),
      insertOrderedList: document.queryCommandState("insertOrderedList"),
    });
  };

  const handleInput = () => {
    onChange(editorRef.current?.innerHTML || "");
    updateActiveFormats();
  };

  const handleKeyDown = (e) => {
    if (e.key === "Tab") { e.preventDefault(); document.execCommand("insertHTML", false, "&nbsp;&nbsp;&nbsp;&nbsp;"); }
  };

  // Seed initial value once
  const seededRef = useRef(false);
  const setRef = (el) => {
    editorRef.current = el;
    if (el && !seededRef.current && value) {
      el.innerHTML = value;
      seededRef.current = true;
    }
  };

  return (
    <div style={{ border: `1px solid ${c.border}`, borderRadius: "10px", overflow: "hidden", background: c.surface }}>
      {/* Toolbar */}
      <div style={{
        display: "flex", flexWrap: "wrap", gap: "2px", padding: "8px 10px",
        borderBottom: `1px solid ${c.border}`, background: c.bg, alignItems: "center",
      }}>
        {TOOLBAR.map((btn, i) => {
          if (btn.type === "sep") return <div key={i} style={{ width: 1, height: 20, background: c.border, margin: "0 4px" }} />;
          const active = activeFormats[btn.cmd];
          return (
            <button
              key={i}
              type="button"
              title={btn.title}
              onMouseDown={(e) => { e.preventDefault(); exec(btn.cmd, btn.arg); }}
              style={{
                ...btn.style,
                minWidth: 28, height: 28, padding: "0 6px",
                border: active ? `1px solid ${c.accentBorder}` : "1px solid transparent",
                borderRadius: "6px", cursor: "pointer", fontSize: "12px",
                background: active ? c.accentLight : "transparent",
                color: active ? c.accent : c.textMuted,
                transition: "all 0.12s", fontFamily: "inherit",
                display: "inline-flex", alignItems: "center", justifyContent: "center",
              }}
              onMouseEnter={e => { if (!active) { e.currentTarget.style.background = c.border; e.currentTarget.style.color = c.text; }}}
              onMouseLeave={e => { if (!active) { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = c.textMuted; }}}
            >
              {btn.icon}
            </button>
          );
        })}
      </div>

      {/* Editable area */}
      <div
        ref={setRef}
        contentEditable
        suppressContentEditableWarning
        onInput={handleInput}
        onKeyUp={updateActiveFormats}
        onMouseUp={updateActiveFormats}
        onKeyDown={handleKeyDown}
        style={{
          minHeight: "280px", padding: "16px 18px", outline: "none",
          fontSize: "14px", lineHeight: "1.75", color: c.text,
          fontFamily: "'Inter', system-ui, sans-serif",
        }}
        data-placeholder="Write your blog content here…"
      />

      {/* Word count */}
      <div style={{
        padding: "6px 14px", borderTop: `1px solid ${c.border}`, background: c.bg,
        fontSize: "11px", color: c.textFaint, textAlign: "right",
      }}>
        {(value.replace(/<[^>]*>/g, "").trim().split(/\s+/).filter(Boolean).length)} words
      </div>
    </div>
  );
};

// ─── IMAGE DROPZONE ───────────────────────────────────────────────────────────
const ImageDropzone = ({ file, preview, onFile }) => {
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef(null);

  const handleDrop = (e) => {
    e.preventDefault(); setDragging(false);
    const f = e.dataTransfer.files?.[0];
    if (f && f.type.startsWith("image/")) onFile(f);
  };

  return (
    <div
      onClick={() => inputRef.current?.click()}
      onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
      onDragLeave={() => setDragging(false)}
      onDrop={handleDrop}
      style={{
        border: `2px dashed ${dragging ? c.accent : c.border}`,
        borderRadius: "12px", cursor: "pointer", overflow: "hidden",
        background: dragging ? c.accentLight : c.bg,
        transition: "all 0.15s", position: "relative",
        minHeight: preview ? "auto" : "160px",
        display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
      }}
    >
      <input ref={inputRef} type="file" accept="image/*" style={{ display: "none" }}
        onChange={e => { const f = e.target.files?.[0]; if (f) onFile(f); }} />

      {preview ? (
        <div style={{ position: "relative", width: "100%" }}>
          <img src={preview} alt="Preview" style={{ width: "100%", maxHeight: "280px", objectFit: "cover", display: "block", borderRadius: "10px" }} />
          <div style={{
            position: "absolute", inset: 0, background: "rgba(0,0,0,0.45)", borderRadius: "10px",
            display: "flex", alignItems: "center", justifyContent: "center",
            opacity: 0, transition: "opacity 0.15s",
          }}
            onMouseEnter={e => e.currentTarget.style.opacity = 1}
            onMouseLeave={e => e.currentTarget.style.opacity = 0}
          >
            <span style={{ color: "#fff", fontSize: "13px", fontWeight: 600, background: "rgba(0,0,0,0.5)", padding: "8px 14px", borderRadius: "8px" }}>
              🖼️ Change Image
            </span>
          </div>
        </div>
      ) : (
        <div style={{ textAlign: "center", padding: "32px 20px" }}>
          <div style={{ fontSize: "32px", marginBottom: "10px" }}>🖼️</div>
          <div style={{ fontSize: "13px", fontWeight: 600, color: c.textMuted, marginBottom: "4px" }}>
            {dragging ? "Drop image here" : "Click or drag & drop to upload"}
          </div>
          <div style={{ fontSize: "11px", color: c.textFaint }}>PNG, JPG, WEBP — max 5MB</div>
        </div>
      )}
    </div>
  );
};

// ─── MAIN ─────────────────────────────────────────────────────────────────────
const BlogCreate = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState({ title: "", desc: "", image: "" });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState({ text: "", type: "" });

  const showToast = (text, type = "error") => {
    setToast({ text, type });
    setTimeout(() => setToast({ text: "", type: "" }), 4000);
  };

  const handleImageFile = (f) => {
    setImageFile(f);
    setImagePreview(URL.createObjectURL(f));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { showToast("Title is required."); return; }
    if (!form.desc || form.desc.replace(/<[^>]*>/g, "").trim().length < 10) {
      showToast("Please write a description (at least 10 characters)."); return;
    }
    setSaving(true);
    try {
      let image = form.image;
      if (imageFile) {
        const uploadResult = await ImageUpload(imageFile);
        if (uploadResult?.success && uploadResult.image) image = uploadResult.image;
      }
      const result = await fetchData(END_POINT.CreateBlog, METHODS.POST, { title: form.title, desc: form.desc, image });
      if (result?.success) {
        showToast("Blog created successfully!", "success");
        setTimeout(() => navigate("/admin/blogs"), 1200);
      } else {
        showToast(result?.message || "Failed to create blog.");
      }
    } catch { showToast("Failed to create blog."); }
    finally { setSaving(false); }
  };

  const wordCount = form.desc.replace(/<[^>]*>/g, "").trim().split(/\s+/).filter(Boolean).length;

  return (
    <div style={{ minHeight: "100vh", background: c.bg, fontFamily: "'Inter', system-ui, sans-serif" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
        * { box-sizing: border-box; }
        [contenteditable]:empty:before { content: attr(data-placeholder); color: #A0AEC0; pointer-events: none; }
        [contenteditable] h2 { font-size: 1.4em; font-weight: 700; margin: 1em 0 0.4em; }
        [contenteditable] h3 { font-size: 1.15em; font-weight: 600; margin: 0.9em 0 0.3em; }
        [contenteditable] ul, [contenteditable] ol { padding-left: 1.5em; margin: 0.5em 0; }
        [contenteditable] li { margin-bottom: 0.2em; }
        [contenteditable] a { color: #4F46E5; text-decoration: underline; }
        @keyframes slideIn { from { opacity: 0; transform: translateX(20px); } to { opacity: 1; transform: translateX(0); } }
        ::-webkit-scrollbar { width: 5px; }
        ::-webkit-scrollbar-thumb { background: #CBD5E0; border-radius: 10px; }
      `}</style>

      <TopBar />
      <Toast msg={toast} />

      <div style={{ maxWidth: "1400px", margin: "0 auto", padding: "28px 24px" }}>

        {/* ── Header ── */}
        <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "28px" }}>
          <button
            onClick={() => navigate("/admin/blogs")}
            style={{ background: "none", border: "none", cursor: "pointer", color: c.textMuted, fontSize: "20px", padding: "4px 6px", borderRadius: "6px", lineHeight: 1, fontFamily: "inherit" }}
            title="Back to blogs"
          >←</button>
          <div style={{ width: 1, height: 24, background: c.border }} />
          <div style={{ width: 34, height: 34, borderRadius: "9px", background: "linear-gradient(135deg, #4F46E5, #7C3AED)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "16px" }}>📝</div>
          <div>
            <h1 style={{ margin: 0, fontSize: "20px", fontWeight: 700, color: c.text }}>Create New Blog</h1>
            <p style={{ margin: 0, fontSize: "12px", color: c.textMuted }}>Fill in the details below to publish a new post.</p>
          </div>
        </div>

        <form onSubmit={submit}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 320px", gap: "16px", alignItems: "start" }}>

            {/* ── Left Column ── */}
            <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>

              {/* Title card */}
              <div style={{ background: c.surface, border: `1px solid ${c.border}`, borderRadius: "14px", padding: "20px", boxShadow: "0 1px 4px rgba(0,0,0,0.04)" }}>
                <Field label="Blog Title" required>
                  <input
                    value={form.title}
                    onChange={e => setForm({ ...form, title: e.target.value })}
                    placeholder="Enter a compelling title…"
                    maxLength={150}
                    style={{
                      width: "100%", padding: "10px 12px", fontSize: "15px", fontWeight: 500,
                      border: `1px solid ${c.border}`, borderRadius: "8px",
                      background: c.bg, color: c.text, outline: "none", fontFamily: "inherit",
                      transition: "border-color 0.15s, box-shadow 0.15s",
                    }}
                    onFocus={e => { e.target.style.borderColor = c.accent; e.target.style.boxShadow = `0 0 0 3px ${c.accentLight}`; }}
                    onBlur={e => { e.target.style.borderColor = c.border; e.target.style.boxShadow = "none"; }}
                  />
                  <div style={{ textAlign: "right", fontSize: "11px", color: c.textFaint, marginTop: "4px" }}>{form.title.length} / 150</div>
                </Field>
              </div>

              {/* Editor card */}
              <div style={{ background: c.surface, border: `1px solid ${c.border}`, borderRadius: "14px", padding: "20px", boxShadow: "0 1px 4px rgba(0,0,0,0.04)" }}>
                <Field label="Content" required hint={`${wordCount} words`}>
                  <RichEditor
                    value={form.desc}
                    onChange={v => setForm({ ...form, desc: v })}
                  />
                </Field>
              </div>
            </div>

            {/* ── Right Column ── */}
            <div style={{ display: "flex", flexDirection: "column", gap: "16px", position: "sticky", top: "16px" }}>

              {/* Publish card */}
              <div style={{ background: c.surface, border: `1px solid ${c.border}`, borderRadius: "14px", padding: "18px", boxShadow: "0 1px 4px rgba(0,0,0,0.04)" }}>
                <div style={{ fontSize: "11px", fontWeight: 700, color: c.textFaint, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: "14px" }}>Publish</div>
                <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                  <button
                    type="submit"
                    disabled={saving}
                    style={{
                      width: "100%", padding: "11px", borderRadius: "9px", border: "none",
                      background: saving ? "#818CF8" : c.accent, color: "#fff", fontSize: "14px",
                      fontWeight: 600, cursor: saving ? "not-allowed" : "pointer",
                      fontFamily: "inherit", display: "flex", alignItems: "center",
                      justifyContent: "center", gap: "8px",
                      boxShadow: saving ? "none" : "0 4px 12px rgba(79,70,229,0.3)",
                      transition: "all 0.15s",
                    }}
                    onMouseEnter={e => { if (!saving) { e.currentTarget.style.background = "#4338CA"; e.currentTarget.style.boxShadow = "0 6px 16px rgba(79,70,229,0.4)"; }}}
                    onMouseLeave={e => { if (!saving) { e.currentTarget.style.background = c.accent; e.currentTarget.style.boxShadow = "0 4px 12px rgba(79,70,229,0.3)"; }}}
                  >
                    {saving ? (
                      <>
                        <div style={{ width: 14, height: 14, border: "2px solid rgba(255,255,255,0.4)", borderTopColor: "#fff", borderRadius: "50%", animation: "spin 0.7s linear infinite" }} />
                        Publishing…
                      </>
                    ) : "🚀 Publish Blog"}
                  </button>
                  <button
                    type="button"
                    onClick={() => navigate("/admin/blogs")}
                    style={{
                      width: "100%", padding: "10px", borderRadius: "9px",
                      border: `1px solid ${c.border}`, background: "transparent",
                      color: c.textMuted, fontSize: "13px", fontWeight: 600,
                      cursor: "pointer", fontFamily: "inherit", transition: "all 0.15s",
                    }}
                    onMouseEnter={e => { e.currentTarget.style.background = c.bg; e.currentTarget.style.color = c.text; }}
                    onMouseLeave={e => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = c.textMuted; }}
                  >Cancel</button>
                </div>

                {/* Checklist */}
                <div style={{ marginTop: "16px", paddingTop: "14px", borderTop: `1px solid ${c.border}` }}>
                  {[
                    { label: "Title added", ok: form.title.trim().length > 0 },
                    { label: "Content written", ok: wordCount >= 10 },
                    { label: "Cover image", ok: !!imageFile },
                  ].map((item, i) => (
                    <div key={i} style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "6px", fontSize: "12px" }}>
                      <div style={{
                        width: 16, height: 16, borderRadius: "50%", flexShrink: 0,
                        display: "flex", alignItems: "center", justifyContent: "center", fontSize: "9px",
                        background: item.ok ? c.successLight : c.border,
                        color: item.ok ? c.success : c.textFaint,
                        border: `1px solid ${item.ok ? "#A7F3D0" : c.border}`,
                        transition: "all 0.2s",
                      }}>{item.ok ? "✓" : ""}</div>
                      <span style={{ color: item.ok ? c.text : c.textFaint }}>{item.label}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Cover image card */}
              <div style={{ background: c.surface, border: `1px solid ${c.border}`, borderRadius: "14px", padding: "18px", boxShadow: "0 1px 4px rgba(0,0,0,0.04)" }}>
                <div style={{ fontSize: "11px", fontWeight: 700, color: c.textFaint, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: "12px" }}>Cover Image</div>
                <ImageDropzone file={imageFile} preview={imagePreview} onFile={handleImageFile} />
                {imageFile && (
                  <div style={{ marginTop: "8px", fontSize: "11px", color: c.textMuted, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "180px" }}>{imageFile.name}</span>
                    <button
                      type="button"
                      onClick={() => { setImageFile(null); setImagePreview(null); }}
                      style={{ background: "none", border: "none", cursor: "pointer", color: c.danger, fontSize: "11px", fontWeight: 600, fontFamily: "inherit", flexShrink: 0 }}
                    >Remove</button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </form>
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default BlogCreate;